
import Vue from "vue"
import axios, { AxiosResponse } from "axios"
import { EventBus } from "@/plugins/eventBus"
import { Company } from "@/types/Company"
import { transformCompanyDataArray, filterApiCompaniesByDistance } from "@/utils/companyTransformer"
import { getActivityLabel } from "@/utils/companyDisplay"
import { DataTableHeader } from 'vuetify'
import { state } from "@/plugins/state"

interface SearchParams {
  search: string;
  page: number;
  section_activite_principale?: string;
  activite_principale?: string;
  tranche_effectif_salarie?: string;
  nature_juridique?: string;
  nom_personne?: string;
  prenoms_personne?: string;
  code_postal?: string;
  departement?: string;
  region?: string;
  ca_min?: number;
  ca_max?: number;
  resultat_net_min?: number;
  resultat_net_max?: number;
}

interface SelectOption {
  value: string | number
  text: string
}

type ActivitySectionOption = SelectOption
type ApeNafOption = SelectOption
type EmployeeCountOption = SelectOption
type LegalFormOption = SelectOption

export default Vue.extend({
  name: "CompanySearch",
  data() {
    return {
      searchValue: "",
      selectedActivitySection: [] as string[],
      selectedApeNafCodes: [] as string[],
      selectedEmployeeCounts: [] as string[],
      selectedLegalForms: [] as string[],
      managerName: '',
      managerFirstname: '',
      caMin: null as number | null,
      caMax: null as number | null,
      resultatNetMin: null as number | null,
      resultatNetMax: null as number | null,
      companies: [] as Company[],
      loading: false,
      hasSearched: false,
      isCancelled: false,
      cachedApeNafOptions: [] as ApeNafOption[],
      selectedDistance: null as number | null,
      postalCode: '',
      selectedDepartments: [] as string[],
      selectedRegions: [] as string[],
      searchProgress: {
        currentPage: 0,
        totalPages: 0,
        isSearching: false
      }
    }
  },
  computed: {
    headers(): Array<DataTableHeader> {
      return [
        { text: this.$vuetify.lang.t('$vuetify.company_name'), value: 'name', width: '25%' },
        { text: this.$vuetify.lang.t('$vuetify.trade_name'), value: 'trade_name', width: '20%' },
        { text: this.$vuetify.lang.t('$vuetify.complete_name'), value: 'complete_name', width: '20%' },
        { text: this.$vuetify.lang.t('$vuetify.manager'), value: 'manager_name', width: '15%' },
        { text: this.$vuetify.lang.t('$vuetify.address'), value: 'address', width: '15%' },
        { text: this.$vuetify.lang.t('$vuetify.activity'), value: 'ape_code', width: '15%' },
        { text: this.$vuetify.lang.t('$vuetify.action'), value: 'actions', sortable: false, width: '80px' }
      ]
    },
    activitySectionOptions(): ActivitySectionOption[] {
      // Access the main_activity_section_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.main_activity_section_codes

      return Object.entries(codes).map(([code, description]) => ({
        value: code,
        text: description as string
      })).sort((a, b) => a.text.localeCompare(b.text))
    },
    apeNafOptions(): ApeNafOption[] {
      return this.cachedApeNafOptions
    },
    employeeCountOptions(): EmployeeCountOption[] {
      // Access the employee_count_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.employee_count_codes

      return Object.entries(codes).map(([code, description]) => ({
        value: code,
        text: description as string
      })).sort((a, b) => {
        // Sort by the numeric value of the code (e.g., "00", "01", "02", etc.)
        // Handle special case "NN" (non-employer) which should come first
        if (a.value === 'NN') return -1
        if (b.value === 'NN') return 1
        return a.value.localeCompare(b.value)
      })
    },
    legalFormOptions(): LegalFormOption[] {
      // Access the legal_form_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.legal_form_codes

      return Object.entries(codes).map(([code, description]) => ({
        value: code,
        text: description as string
      })).sort((a, b) => a.text.localeCompare(b.text))
    },
    departmentOptions(): SelectOption[] {
      // Access the departments_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.departments_codes

      return Object.entries(codes).map(([code, description]) => ({
        value: code,
        text: `${description} (${code})`
      })).sort((a, b) => a.text.localeCompare(b.text))
    },
    regionOptions(): SelectOption[] {
      // Access the regions_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.regions_codes

      return Object.entries(codes).map(([code, description]) => ({
        value: code,
        text: description as string
      })).sort((a, b) => a.text.localeCompare(b.text))
    },
    hasActiveFilters(): boolean {
      return this.selectedActivitySection.length > 0 || 
             this.selectedApeNafCodes.length > 0 || 
             this.selectedEmployeeCounts.length > 0 ||
             this.selectedLegalForms.length > 0 ||
             this.selectedDepartments.length > 0 ||
             this.selectedRegions.length > 0 ||
             this.postalCode.trim() !== '' ||
             this.managerName.trim() !== '' ||
             this.managerFirstname.trim() !== '' ||
             this.caMin !== null ||
             this.caMax !== null ||
             this.resultatNetMin !== null ||
             this.resultatNetMax !== null ||
             this.selectedDistance !== null
    },
    distanceOptions(): SelectOption[] {
      return [
        { value: 5, text: '5 km' },
        { value: 10, text: '10 km' },
        { value: 30, text: '30 km' },
        { value: 50, text: '50 km' },
        { value: 100, text: '100 km' },
        { value: -1, text: this.$vuetify.lang.t('$vuetify.all_of_france') }
      ]
    },
    spaceAddressPosition(): { latitude: number; longitude: number } | null {
      return state.spaceAddressPosition
    },
    canUseDistanceFilter(): boolean {
      // Distance filter can be used if:
      // 1. Space has a position AND
      // 2. At least one of the location filters (postal code, department, region) is active
      const hasSpacePosition = !!this.spaceAddressPosition
      const hasLocationFilters = this.postalCode.trim() !== '' || 
                               this.selectedDepartments.length > 0 || 
                               this.selectedRegions.length > 0
      
      return hasSpacePosition && hasLocationFilters
    },
    distanceFilterDisabledReason(): string {
      if (!this.spaceAddressPosition) {
        return this.$vuetify.lang.t('$vuetify.distance_filter_no_space_position')
      }
      
      const hasLocationFilters = this.postalCode.trim() !== '' || 
                               this.selectedDepartments.length > 0 || 
                               this.selectedRegions.length > 0
      
      if (!hasLocationFilters) {
        return this.$vuetify.lang.t('$vuetify.distance_filter_no_location_filters')
      }
      
      return ''
    },
    searchProgressPercentage(): number {
      if (!this.searchProgress.isSearching || this.searchProgress.totalPages === 0) {
        return 0
      }
      return Math.round((this.searchProgress.currentPage / this.searchProgress.totalPages) * 100)
    },

  },
  watch: {
    '$vuetify.lang.current': {
      handler() {
        this.updateCachedApeNafOptions()
      },
      immediate: true
    },
    postalCode: 'checkClearDistanceFilter',
    selectedDepartments: {
      handler: 'checkClearDistanceFilter',
      deep: true
    },
    selectedRegions: {
      handler: 'checkClearDistanceFilter',
      deep: true
    }
  },
  methods: {
    /**
     * Build search parameters from all active filters
     */
    buildSearchParams(searchTerm: string, page = 1): SearchParams {
      const params: SearchParams = {
        search: searchTerm,
        page
      }

      // Collect all filter values in a local object for type-safe access
      const filterValues = {
        selectedActivitySection: this.selectedActivitySection,
        selectedApeNafCodes: this.selectedApeNafCodes,
        selectedEmployeeCounts: this.selectedEmployeeCounts,
        selectedLegalForms: this.selectedLegalForms,
        selectedDepartments: this.selectedDepartments,
        selectedRegions: this.selectedRegions,
        managerName: this.managerName,
        managerFirstname: this.managerFirstname,
        postalCode: this.postalCode
      }

      // Helper to add array-based filters
      const addArrayFilter = (key: keyof typeof filterValues, paramName: keyof SearchParams) => {
        const arr = filterValues[key] as string[]
        if (Array.isArray(arr) && arr.length > 0) {
          (params as any)[paramName] = arr.join(',')
        }
      }

      // Array-based filters
      const arrayFilters: [keyof typeof filterValues, keyof SearchParams][] = [
        ['selectedActivitySection', 'section_activite_principale'],
        ['selectedApeNafCodes', 'activite_principale'],
        ['selectedEmployeeCounts', 'tranche_effectif_salarie'],
        ['selectedLegalForms', 'nature_juridique'],
        ['selectedDepartments', 'departement'],
        ['selectedRegions', 'region']
      ]
      arrayFilters.forEach(([key, param]) => addArrayFilter(key, param))

      // String-based filters
      const stringFilters: [keyof typeof filterValues, keyof SearchParams][] = [
        ['managerName', 'nom_personne'],
        ['managerFirstname', 'prenoms_personne'],
        ['postalCode', 'code_postal']
      ]
      stringFilters.forEach(([key, param]) => {
        const val = (filterValues[key] as string)?.trim()
        if (val) {
          (params as any)[param] = val
        }
      })

      // Numeric filters
      if (this.caMin !== null && this.caMin > 0) {
        params.ca_min = this.caMin
      }
      if (this.caMax !== null && this.caMax > 0) {
        params.ca_max = this.caMax
      }
      if (this.resultatNetMin !== null) {
        params.resultat_net_min = this.resultatNetMin
      }
      if (this.resultatNetMax !== null) {
        params.resultat_net_max = this.resultatNetMax
      }

      return params
    },

    /**
     * Clear all filter arrays
     */
    clearAllFilterArrays() {
      // Clear all filter arrays
      this.selectedActivitySection = []
      this.selectedApeNafCodes = []
      this.selectedEmployeeCounts = []
      this.selectedLegalForms = []
      this.selectedDepartments = []
      this.selectedRegions = []
      
      // Clear manager name and firstname
      this.managerName = ''
      this.managerFirstname = ''
      
      // Clear postal code
      this.postalCode = ''
      
      // Reset revenue range to default values
      this.caMin = null
      this.caMax = null
      
      // Reset net income range to default values
      this.resultatNetMin = null
      this.resultatNetMax = null
      
      // Reset distance filter
      this.selectedDistance = null
    },

    /**
     * Update cached APE NAF options when locale changes
     */
    updateCachedApeNafOptions() {
      // Access the ape_naf_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.ape_naf_codes
      
      // Only keep codes that match the valid search pattern:
      // pattern matches APE/NAF codes in format: 2 digits . 2 digits and 1 uppercase letter (e.g., '47.51Z')
      const validCodeRegex = /^\d{2}\.\d{2}[A-Z]$/
      
      this.cachedApeNafOptions = Object.entries(codes)
        .filter(([code]) => validCodeRegex.test(code))
        .map(([code, description]) => ({
          value: code,
          text: `${description} (${code})`
        }))
        .sort((a, b) => a.value.localeCompare(b.value))
    },

    /**
     * Clear all active filters
     */
    clearAllFilters() {
      this.clearAllFilterArrays()
      this.managerName = ''
      this.managerFirstname = ''
    },

    /**
     * Perform the search for companies
     */
    async performSearch() {
      if (!this.searchValue || this.searchValue.trim() === '') {
        return
      }

      // Reset cancellation flag
      this.isCancelled = false

      this.loading = true
      this.companies = []
      this.hasSearched = true
      
      // Reset search progress
      this.searchProgress = {
        currentPage: 0,
        totalPages: 0,
        isSearching: false
      }

      try {
        await this.searchCompanies(this.searchValue.trim())
      } catch (error) {
        if (!this.isCancelled) {
          // Only show error if it's not a cancellation
          EventBus.$emit('snackbar', { axiosError: error })
        }
      } finally {
        this.loading = false
        this.searchProgress.isSearching = false
      }
    },

    /**
     * Cancel the ongoing search
     */
    cancelSearch() {
      this.isCancelled = true
      this.loading = false
    },

    /**
     * Search companies with client-side distance filtering
     * @param searchTerm The search term to use
     */
    async searchCompanies(searchTerm: string) {
      try {
        // Check if search was cancelled
        if (this.isCancelled) {
          return
        }

        // Build search parameters using unified method
        const searchParams = this.buildSearchParams(searchTerm, 1)

        // First, get the first page to determine total pages
        const firstPageResponse: AxiosResponse = await axios.get('/v1/company/search', {
          params: searchParams
        })

        const totalPages = firstPageResponse.data.total_pages ?? 1
        const firstPageCompanies = firstPageResponse.data.results ?? []
        
        // Initialize search progress
        this.searchProgress = {
          currentPage: 1,
          totalPages: totalPages,
          isSearching: true
        }

        // Check if distance filter is active
        const hasDistanceFilter = this.selectedDistance !== null && this.selectedDistance !== -1
        
        if (hasDistanceFilter) {
          // Apply distance filtering and fetch additional pages if needed
          await this.searchWithDistanceFilter(searchTerm, firstPageCompanies, totalPages)
        } else {
          // No distance filter - fetch all pages to get complete results
          await this.searchWithoutDistanceFilter(searchTerm, firstPageCompanies, totalPages)
        }

      } catch (error) {
        if (!this.isCancelled) {
          // Show error snackbar only if not cancelled
          EventBus.$emit('snackbar', { axiosError: error })
          this.companies = []
        }
        throw error // Re-throw to be handled by the calling method
      }
    },

    /**
     * Fetches and filters paginated company results with distance filtering.
     * Handles the loop, delay, and accumulation logic for searchWithDistanceFilter.
     */
    async fetchAndFilterPages(
      searchTerm: string,
      totalPages: number,
      targetResults: number,
      initialFiltered: any[],
    ): Promise<any[]> {
      let allFilteredResults = initialFiltered
      let currentPage = 1
      while (allFilteredResults.length < targetResults && currentPage < totalPages && !this.isCancelled) {
        currentPage++
        this.searchProgress.currentPage = currentPage
        const nextPageParams = this.buildSearchParams(searchTerm, currentPage)
        const nextPageResponse: AxiosResponse = await axios.get('/v1/company/search', {
          params: nextPageParams
        })
        const nextPageCompanies = nextPageResponse.data.results ?? []
        if (nextPageCompanies.length === 0) break // No more pages
        const nextPageFiltered = filterApiCompaniesByDistance(
          nextPageCompanies,
          this.spaceAddressPosition,
          this.selectedDistance ?? -1
        )
        allFilteredResults = [...allFilteredResults, ...nextPageFiltered]
        // Add small delay to avoid overwhelming the server
        if (currentPage < totalPages) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }
      return allFilteredResults
    },

    /**
     * Search with distance filtering - fetch pages until we have enough filtered results
     * Handles the first page, then delegates the loop/delay logic to fetchAndFilterPages.
     */
    async searchWithDistanceFilter(searchTerm: string, firstPageCompanies: any[], totalPages: number) {
      const targetResults = 25 // Target number of results to display
      // Filter first page companies (raw API data)
      const firstPageFiltered = filterApiCompaniesByDistance(
        firstPageCompanies,
        this.spaceAddressPosition,
        this.selectedDistance ?? -1
      )
      // Delegate the rest to the helper
      const allFilteredResults = await this.fetchAndFilterPages(
        searchTerm,
        totalPages,
        targetResults,
        firstPageFiltered
      )
      // Transform filtered results and take only the first targetResults
      const transformedResults = transformCompanyDataArray(allFilteredResults, this.$vuetify.lang.current)
      this.companies = transformedResults.slice(0, targetResults)
    },

    /**
     * Search without distance filtering - fetch all pages to get complete results
     */
    async searchWithoutDistanceFilter(searchTerm: string, firstPageCompanies: any[], totalPages: number) {
      // Transform first page results
      this.companies = transformCompanyDataArray(firstPageCompanies, this.$vuetify.lang.current)

      // If there are more pages, fetch them in chunks and add to display
      if (totalPages > 1 && !this.isCancelled) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
        await this.processChunkedCompanyCalls(searchTerm, remainingPages)
      }
    },

    /**
     * Process company API calls in chunks to avoid overwhelming the server
     * @param searchTerm - The search term
     * @param pages - Array of page numbers to fetch
     * @param chunkSize - Number of calls to execute simultaneously (default: 1)
     * @param delay - Delay between chunks in milliseconds (default: 100)
     */
    async processChunkedCompanyCalls(
      searchTerm: string,
      pages: number[],
      chunkSize = 1,
      delay = 100
    ): Promise<void> {
      for (let i = 0; i < pages.length; i += chunkSize) {
        // Check if search was cancelled
        if (this.isCancelled) {
          return
        }

        const chunk = pages.slice(i, i + chunkSize)
        const chunkPromises = chunk.map(page => {
          // Build search parameters using unified method
          const params = this.buildSearchParams(searchTerm, page)
          
          // Convert to the format expected by the API for pagination calls
          const apiParams: any = {
            search: params.search,
            page: params.page
          }

          // Map search parameters to API parameter names
          if (params.section_activite_principale) {
            apiParams.activity_section = params.section_activite_principale
          }
          if (params.activite_principale) {
            apiParams.ape_naf_codes = params.activite_principale
          }
          if (params.tranche_effectif_salarie) {
            apiParams.tranche_effectif_salarie = params.tranche_effectif_salarie
          }
          if (params.nature_juridique) {
            apiParams.nature_juridique = params.nature_juridique
          }
          if (params.nom_personne) {
            apiParams.nom_personne = params.nom_personne
          }
          if (params.prenoms_personne) {
            apiParams.prenoms_personne = params.prenoms_personne
          }
          if (params.ca_min) {
            apiParams.ca_min = params.ca_min
          }
          if (params.ca_max) {
            apiParams.ca_max = params.ca_max
          }
          if (params.resultat_net_min) {
            apiParams.resultat_net_min = params.resultat_net_min
          }
          if (params.resultat_net_max) {
            apiParams.resultat_net_max = params.resultat_net_max
          }

          return axios.get('/v1/company/search', { params: apiParams })
        })

        try {
          const chunkResults = await Promise.all(chunkPromises)

          // Transform and add companies from this chunk to the display
          for (const response of chunkResults) {
            const companiesData = response.data.results ?? []
            const transformedCompanies = transformCompanyDataArray(companiesData, this.$vuetify.lang.current)
            this.companies = [...this.companies, ...transformedCompanies]
          }
          
          // Update progress
          this.searchProgress.currentPage = Math.min(this.searchProgress.currentPage + chunk.length, this.searchProgress.totalPages)
        } catch (error) {
          if (this.isCancelled) {
            return // Exit gracefully if cancelled
          }
          throw error // Re-throw other errors
        }

        // Add delay between chunks (except for the last chunk)
        if (i + chunkSize < pages.length) {
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
    },

    /**
     * Clear the search and results
     */
    clearSearch() {
      // Cancel any ongoing search
      this.isCancelled = true

      this.searchValue = ""
      this.clearAllFilterArrays()
      this.companies = []
      this.hasSearched = false
      this.loading = false
      this.searchProgress = {
        currentPage: 0,
        totalPages: 0,
        isSearching: false
      }
      this.$emit('input', null)
    },

    /**
     * Get localized APE code label
     * @param apeCode The APE code
     * @returns The localized activity label
     */
    getLocalizedApeCode(apeCode: string): string {
      return getActivityLabel(apeCode, this.$vuetify.lang.current)
    },

    removeFilterItem(item: any, filterType: string) {
      const allFilterTypes = ['activitySection', 'apeNafCode', 'employeeCount', 'legalForm', 'department', 'region']
      if (allFilterTypes.includes(filterType)) {
        switch (filterType) {
          case 'activitySection':
            this.selectedActivitySection = this.selectedActivitySection.filter(
              (v: string) => v !== item.value
            )
            break
          case 'apeNafCode':
            this.selectedApeNafCodes = this.selectedApeNafCodes.filter(
              (v: string) => v !== item.value
            )
            break
          case 'employeeCount':
            this.selectedEmployeeCounts = this.selectedEmployeeCounts.filter(
              (v: string) => v !== item.value
            )
            break
          case 'legalForm':
            this.selectedLegalForms = this.selectedLegalForms.filter(
              (v: string) => v !== item.value
            )
            break
          case 'department':
            this.selectedDepartments = this.selectedDepartments.filter(
              (v: string) => v !== item.value
            )
            break
          case 'region':
            this.selectedRegions = this.selectedRegions.filter(
              (v: string) => v !== item.value
            )
            break
        }
      }
    },

    /**
     * Remove distance filter
     */
    removeDistanceFilter() {
      this.selectedDistance = null
    },
    checkClearDistanceFilter() {
      if (
        this.selectedDistance !== null &&
        this.selectedDistance !== undefined &&
        this.selectedDistance !== -1 &&
        this.postalCode.trim() === '' &&
        this.selectedDepartments.length === 0 &&
        this.selectedRegions.length === 0
      ) {
        this.selectedDistance = null;
      }
    },
    logCompany(company: Company) {
      console.log('Company object:', company)
    },
  }
})
